import { useState } from 'react'
import { Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import {
  Alert,
  Chip,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import { useGetAdminEstablishmentQuery } from '@/core/services/admin'
import { useListExternalHealthDataWorklistQuery } from '@/core/services/establishment'
import { useGetStaffQuery } from '@/core/services/staff'
import type {
  ExternalHealthDataWorklist,
  ExternalHealthDataWorklistReason,
  ExternalHealthDataWorklistStatus,
} from '@/core/models/external-health-data-worklist.model'

const PAGE_SIZE = 7

export function InsWorklist() {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const { data: establishment } = useGetAdminEstablishmentQuery()
  const establishmentId = establishment?._id

  const [page, setPage] = useState(1)
  const skip = (page - 1) * PAGE_SIZE

  const { data, isLoading, isFetching, error } = useListExternalHealthDataWorklistQuery(
    { establishmentId: establishmentId!, limit: PAGE_SIZE, skip },
    { skip: !establishmentId }
  )

  const entries = data?.items ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const rangeStart = totalCount === 0 ? 0 : skip + 1
  const rangeEnd = Math.min(skip + entries.length, totalCount)

  if (error) {
    return (
      <Stack p={3}>
        <Alert severity="error">
          <Trans>Failed to load INS worklist</Trans>
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack spacing={3} p={{ xs: 2, sm: 3 }}>
      <Stack spacing={0.5}>
        <Typography component="div" variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
          <Trans>Liste de travail INSi</Trans>
        </Typography>
        <Typography component="div" variant="body2" color="text.secondary">
          <Trans>
            Identities that received a KO return from INSi: identities of the repository downgraded to provisional identity after a verification, and health
            data received from another identification domain whose INSi call failed. To be analysed manually.
          </Trans>
        </Typography>
      </Stack>

      {isMobile ? <MobileList entries={entries} isLoading={isLoading || isFetching} /> : <DesktopTable entries={entries} isLoading={isLoading || isFetching} />}

      {totalCount > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ px: 1 }}>
          <Typography component="div" variant="caption" color="text.secondary">
            <Trans>
              Affichage de {rangeStart}–{rangeEnd} sur {totalCount}
            </Trans>
          </Typography>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size="small" siblingCount={isMobile ? 0 : 1} />
        </Stack>
      )}
    </Stack>
  )
}

type ListProps = {
  entries: ExternalHealthDataWorklist[]
  isLoading: boolean
}

function getWorklistReasonLabel(reason?: ExternalHealthDataWorklistReason) {
  switch (reason) {
    case 'IDENTITY_VERIFICATION_KO':
      return <Trans>KO INSi return on verification</Trans>
    case 'EXTERNAL_DATA_INS_CALL_FAILED':
    default:
      return <Trans>Failed INSi call on external data</Trans>
  }
}

function WorklistOrigin({ entry }: { entry: ExternalHealthDataWorklist }) {
  if (entry.reason === 'IDENTITY_VERIFICATION_KO') {
    return (
      <>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {entry.patientFullName || <Trans>Identity of the repository</Trans>}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <Trans>Downgraded to provisional identity</Trans>
        </Typography>
      </>
    )
  }
  return (
    <>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {entry.emitter}
      </Typography>
      {entry.patientFullName && (
        <Typography variant="caption" color="text.secondary">
          {entry.patientFullName}
        </Typography>
      )}
    </>
  )
}

function getWorklistStatusLabel(status: ExternalHealthDataWorklistStatus) {
  switch (status) {
    case 'PENDING_REVIEW':
      return <Trans>Pending review</Trans>
    case 'REVIEWED':
      return <Trans>Reviewed</Trans>
    case 'DISMISSED':
      return <Trans>Dismissed</Trans>
    default:
      return status
  }
}

function StaffName({ staffId }: { staffId: string }) {
  const { data: staff } = useGetStaffQuery(staffId, { skip: !staffId })
  return <>{`${staff?.userInfo?.surname ?? ''} ${staff?.userInfo?.name ?? ''}`.trim()}</>
}

function DesktopTable({ entries, isLoading }: ListProps) {
  return (
    <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
      <Table>
        <TableHead>
          <TableRow sx={{ bgcolor: 'action.hover' }}>
            <TableCell sx={headCellSx}>
              <Trans>Origin</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Reason</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>INS (NIR / OID)</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Saisi par</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Received on</Trans>
            </TableCell>
            <TableCell sx={headCellSx}>
              <Trans>Statut</Trans>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                <Trans>Loading…</Trans>
              </TableCell>
            </TableRow>
          ) : entries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                <Trans>No health data awaiting INS review.</Trans>
              </TableCell>
            </TableRow>
          ) : (
            entries.map((e) => (
              <TableRow key={e._id} sx={{ '&:hover': { bgcolor: 'action.hover' } }}>
                <TableCell>
                  <WorklistOrigin entry={e} />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getWorklistReasonLabel(e.reason)}
                    color={e.reason === 'IDENTITY_VERIFICATION_KO' ? 'error' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', display: 'block' }}>
                    {e.insNIR}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {e.insOID || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <StaffName staffId={e.triggeredBy} />
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                    {new Date(e.createdAt).toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={getWorklistStatusLabel(e.status)}
                    color={e.status === 'PENDING_REVIEW' ? 'warning' : 'default'}
                  />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function MobileList({ entries, isLoading }: ListProps) {
  if (isLoading) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Trans>Loading…</Trans>
      </Paper>
    )
  }
  if (entries.length === 0) {
    return (
      <Paper elevation={0} sx={{ p: 4, textAlign: 'center', color: 'text.secondary', border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Trans>No health data awaiting INS review.</Trans>
      </Paper>
    )
  }
  return (
    <Stack spacing={1.5}>
      {entries.map((e) => (
        <Paper key={e._id} elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
          <Stack spacing={0.5}>
            <WorklistOrigin entry={e} />
            <Chip
              size="small"
              variant="outlined"
              label={getWorklistReasonLabel(e.reason)}
              color={e.reason === 'IDENTITY_VERIFICATION_KO' ? 'error' : 'default'}
            />
            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
              {e.insNIR}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {e.insOID || '—'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              <Trans>Saisi par</Trans> : <StaffName staffId={e.triggeredBy} />
            </Typography>
            <Chip size="small" variant="outlined" label={getWorklistStatusLabel(e.status)} />
            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
              {new Date(e.createdAt).toLocaleString()}
            </Typography>
          </Stack>
        </Paper>
      ))}
    </Stack>
  )
}

const headCellSx = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
}
